import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const LABELS = {
  crm: 'CRM', bookings: 'Bookings', billing: 'Billing', inventory: 'Inventory',
  projects: 'Projects', storage: 'Storage', postmaster: 'PostMaster',
  seomax: 'SEOMax', admix: 'AdMix',
};

function AgentConnections() {
  const { currentUser } = useAuth();
  const [connections, setConnections] = useState([]);
  const [apps, setApps] = useState([]);
  const [name, setName] = useState('');
  const [access, setAccess] = useState({});
  const [created, setCreated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const result = await api.getAgentConnections();
      setConnections(result.connections);
      setApps(result.available_apps);
      setAccess(Object.fromEntries(result.available_apps.map(app => [app, 'none'])));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (!currentUser?.can_manage_agents) return <Navigate to="/" replace />;

  const createConnection = async (event) => {
    event.preventDefault();
    const scopes = [];
    Object.entries(access).forEach(([app, level]) => {
      if (level === 'read' || level === 'write') scopes.push(`${app}:read`);
      if (level === 'write') scopes.push(`${app}:write`);
    });
    if (!scopes.length) {
      setError('Choose access to at least one application.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await api.createAgentConnection(name, scopes);
      setCreated(result);
      setName('');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (connection) => {
    if (!window.confirm(`Revoke “${connection.name}”? It will stop working immediately.`)) return;
    try {
      await api.revokeAgentConnection(connection.id);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const copy = async (value) => {
    await navigator.clipboard.writeText(value);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="container mx-auto max-w-5xl px-6 py-8 space-y-8">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">AI Connections</h2>
          <p className="mt-2 text-slate-600">
            Connect Claude, ChatGPT, or another MCP client to the VentES apps your organization owns.
          </p>
        </div>

        {error && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>}

        {created && (
          <section className="rounded-xl border-2 border-amber-300 bg-amber-50 p-6 shadow-sm">
            <h3 className="text-xl font-semibold text-slate-900">Save this token now</h3>
            <p className="mt-1 text-sm text-slate-700">For security, VentES will not show it again.</p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">MCP URL</label>
                <div className="mt-1 flex gap-2">
                  <code className="min-w-0 flex-1 overflow-x-auto rounded bg-white p-3 text-sm">{created.mcp_url}</code>
                  <button onClick={() => copy(created.mcp_url)} className="rounded bg-slate-800 px-4 text-white">Copy</button>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Bearer token</label>
                <div className="mt-1 flex gap-2">
                  <code className="min-w-0 flex-1 overflow-x-auto rounded bg-white p-3 text-sm">{created.token}</code>
                  <button onClick={() => copy(created.token)} className="rounded bg-slate-800 px-4 text-white">Copy</button>
                </div>
              </div>
            </div>
            <button onClick={() => setCreated(null)} className="mt-4 text-sm font-medium text-slate-700 underline">I saved it</button>
          </section>
        )}

        <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-xl font-semibold text-slate-900">Create a connection</h3>
          <form onSubmit={createConnection} className="mt-5 space-y-5">
            <div>
              <label htmlFor="connection-name" className="block text-sm font-medium text-slate-700">Connection name</label>
              <input id="connection-name" required maxLength="100" value={name}
                onChange={event => setName(event.target.value)} placeholder="Ramsey’s Claude Desktop"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" />
            </div>
            <div className="overflow-hidden rounded-lg border border-slate-200">
              {apps.map(app => (
                <div key={app} className="flex items-center justify-between border-b border-slate-100 px-4 py-3 last:border-b-0">
                  <span className="font-medium text-slate-800">{LABELS[app] || app}</span>
                  <select value={access[app] || 'none'}
                    onChange={event => setAccess({...access, [app]: event.target.value})}
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm">
                    <option value="none">No access</option>
                    <option value="read">Read only</option>
                    <option value="write">Read and write</option>
                  </select>
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-500">Writes are still prepared for confirmation in SS Dash before execution.</p>
            <button disabled={saving} className="rounded-lg bg-blue-700 px-5 py-2.5 font-semibold text-white disabled:opacity-50">
              {saving ? 'Creating…' : 'Create connection'}
            </button>
          </form>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-xl font-semibold text-slate-900">Existing connections</h3>
          {loading ? <p className="mt-4 text-slate-500">Loading…</p> : connections.length === 0 ?
            <p className="mt-4 text-slate-500">No AI connections yet.</p> :
            <div className="mt-4 divide-y divide-slate-100">
              {connections.map(connection => (
                <div key={connection.id} className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <div className="font-semibold text-slate-900">{connection.name}</div>
                    <div className="mt-1 text-sm text-slate-500">
                      {connection.token_prefix}… · {connection.scopes.join(', ')}
                    </div>
                    <div className="mt-1 text-xs text-slate-400">
                      Last used: {connection.last_used_at ? new Date(connection.last_used_at).toLocaleString() : 'Never'}
                    </div>
                  </div>
                  {connection.revoked_at ?
                    <span className="text-sm font-medium text-slate-400">Revoked</span> :
                    <button onClick={() => revoke(connection)} className="self-start rounded border border-red-300 px-3 py-2 text-sm font-medium text-red-700">Revoke</button>}
                </div>
              ))}
            </div>}
        </section>
      </main>
    </div>
  );
}

export default AgentConnections;
